// Transaction utility functions for admin
import { ADMIN_COLORS } from '@/constants/admin';
import { IAdminTransaction } from '@/types/admin/transactions';

/**
 * Get color for payment settlement status
 * Centralized status color mapping for consistency across components
 */
export const getPaymentStatusColor = (status: string): string => {
  const statusColors: { [key: string]: string } = {
    // Success states
    ACCEPTED: ADMIN_COLORS.SUCCESS,
    
    // Pending states
    QUEUED_FOR_CAPTURE: ADMIN_COLORS.TRANSACTION.PENDING,
    
    // Failure states
    DECLINED: ADMIN_COLORS.ERROR,
    REJECTED: ADMIN_COLORS.ERROR,
    NOT_SUPPORTED: ADMIN_COLORS.ERROR,
    
    // Requires attention
    INVALID_USER_DATA: ADMIN_COLORS.TRANSACTION.PENDING,
    
    // Cancelled/Inactive states
    VOIDED: ADMIN_COLORS.TRANSACTION.VOIDED,
    LINE_DOWN: ADMIN_COLORS.TRANSACTION.VOIDED,
  };
  
  return statusColors[status] || ADMIN_COLORS.TEXT_SECONDARY;
};

/**
 * Check if transaction is a payment
 */
export const isPayment = (transaction: IAdminTransaction): boolean => {
  return transaction.type === 'payment';
};

/**
 * Check if transaction is a charge
 */
export const isCharge = (transaction: IAdminTransaction): boolean => {
  return transaction.type === 'charge';
};

/**
 * Get color for transaction amount based on type
 */
export const getTransactionAmountColor = (transaction: IAdminTransaction): string => {
  return isPayment(transaction) 
    ? ADMIN_COLORS.TRANSACTION.PAYMENT 
    : ADMIN_COLORS.TRANSACTION.CHARGE;
};

/**
 * Get transaction type label
 */
export const getTransactionTypeLabel = (transaction: IAdminTransaction): string => {
  return isPayment(transaction) ? 'Payment' : 'Charge';
};

/**
 * Check if transaction has expandable details
 */
export const hasTransactionDetails = (transaction: IAdminTransaction): boolean => {
  return Boolean(
    (transaction.paidByFirstName && transaction.paidByLastName) ||
    transaction.paymentSettlementStatus ||
    (transaction.lateFees?.amount && transaction.lateFees.amount > 0) ||
    (transaction.paymentConvenienceFee && transaction.paymentConvenienceFee > 0) ||
    transaction.postMonth ||
    transaction.arCode
  );
};

/**
 * Format payer name from transaction
 */
export const getPayerName = (transaction: IAdminTransaction): string => {
  if (transaction.paidByFirstName && transaction.paidByLastName) {
    return `${transaction.paidByFirstName} ${transaction.paidByLastName}`;
  }
  return '-';
};

